// T-373 — Editor di una categoria del menù (crea/modifica/elimina).

import {
  IonButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonInput,
  IonItem,
  IonLabel,
  IonModal,
  IonSegment,
  IonSegmentButton,
  IonTitle,
  IonToggle,
  IonToolbar,
} from '@ionic/react';
import { useEffect, useState } from 'react';
import type { MenuCategory, MenuCategoryInput, MenuSection } from '@trackeat/shared';
import { toUserMessage } from '../../../lib/errors.js';
import { confirmDialog } from '../../../lib/ui/dialogs.js';

interface Props {
  isOpen: boolean;
  category: MenuCategory | null; // null = creazione
  itemCount: number; // per il guard di eliminazione
  addCategory: (input: MenuCategoryInput) => Promise<void>;
  editCategory: (cat: MenuCategory, patch: Partial<MenuCategory>) => Promise<MenuCategory>;
  removeCategory: (id: string) => Promise<void>;
  onDone: () => void;
  onClose: () => void;
  setToast: (msg: string) => void;
}

export function CategoryEditorModal(props: Props): JSX.Element {
  const { isOpen, category, itemCount } = props;
  const isEdit = !!category;

  const [name, setName] = useState('');
  const [icon, setIcon] = useState('');
  const [section, setSection] = useState<MenuSection>('food');
  const [visible, setVisible] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setName(category?.name ?? '');
    setIcon(category?.icon ?? '');
    setSection(category?.section ?? 'food');
    setVisible(category?.visible ?? true);
  }, [isOpen, category]);

  async function save(): Promise<void> {
    const trimmed = name.trim();
    if (!trimmed) {
      props.setToast('Nome categoria richiesto');
      return;
    }
    setSaving(true);
    try {
      if (isEdit && category) {
        await props.editCategory(category, { name: trimmed, icon: icon.trim() || null, section, visible });
      } else {
        await props.addCategory({ name: trimmed, icon: icon.trim() || null, section, visible });
      }
      props.onDone();
      props.onClose();
    } catch (err) {
      props.setToast(toUserMessage(err));
    } finally {
      setSaving(false);
    }
  }

  async function del(): Promise<void> {
    if (!category) return;
    if (itemCount > 0) {
      props.setToast(`Prima sposta o elimina i ${itemCount} piatti di questa categoria`);
      return;
    }
    if (!(await confirmDialog(`Eliminare la categoria "${category.name}"?`, { okText: 'Elimina', danger: true }))) return;
    setSaving(true);
    try {
      await props.removeCategory(category.id);
      props.onDone();
      props.onClose();
    } catch (err) {
      props.setToast(toUserMessage(err));
    } finally {
      setSaving(false);
    }
  }

  return (
    <IonModal isOpen={isOpen} onDidDismiss={props.onClose}>
      <IonHeader>
        <IonToolbar>
          <IonTitle>{isEdit ? 'Modifica categoria' : 'Nuova categoria'}</IonTitle>
          <IonButtons slot="end">
            <IonButton onClick={props.onClose}>Annulla</IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>
      <IonContent className="ion-padding">
        <IonItem>
          <IonLabel position="stacked">Nome *</IonLabel>
          <IonInput value={name} onIonInput={(e) => setName(e.detail.value ?? '')} placeholder="Es. Antipasti" />
        </IonItem>
        <IonItem>
          <IonLabel position="stacked">Icona (emoji, facoltativa)</IonLabel>
          <IonInput value={icon} onIonInput={(e) => setIcon(e.detail.value ?? '')} placeholder="🍽️" maxlength={4} />
        </IonItem>

        <IonLabel style={{ display: 'block', margin: '14px 4px 6px', fontSize: 13, color: 'var(--ion-color-medium)' }}>
          Sezione
        </IonLabel>
        <IonSegment value={section} onIonChange={(e) => setSection((e.detail.value as MenuSection) ?? 'food')}>
          <IonSegmentButton value="food">
            <IonLabel>Cibo</IonLabel>
          </IonSegmentButton>
          <IonSegmentButton value="bar">
            <IonLabel>Bar</IonLabel>
          </IonSegmentButton>
        </IonSegment>

        <IonItem lines="none" style={{ marginTop: 12 }}>
          <IonLabel>Visibile nel menù</IonLabel>
          <IonToggle checked={visible} onIonChange={(e) => setVisible(e.detail.checked)} />
        </IonItem>

        <IonButton expand="block" disabled={saving} onClick={() => void save()} style={{ marginTop: 16 }}>
          {isEdit ? 'Salva' : 'Crea categoria'}
        </IonButton>
        {isEdit && (
          <IonButton expand="block" fill="clear" color="danger" disabled={saving} onClick={() => void del()}>
            Elimina categoria
          </IonButton>
        )}
      </IonContent>
    </IonModal>
  );
}
